import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const AFFIRM_RE = /\b(sì|si|ok|okay|va bene|procedi|confermo|conferma|vai|avanti|genera|parti|d'accordo)\b/i;
const NEGATE_RE = /\b(no|annulla|cancella|stop|ferma|cambia|ricomincia)\b/i;

/**
 * Multi-turn voice command state machine.
 *
 * Manages the browser's SpeechRecognition + SpeechSynthesis, orchestrates
 * slot-filling (asking for missing distance/destination), and confirms via
 * yes/no before invoking the caller's onConfirm().
 *
 * @param {object} opts
 * @param {string} opts.lang          Current UI language ("it" | "en").
 * @param {(text:string)=>void} opts.t  i18n translator (for UI hints).
 * @param {(parsed:object)=>Promise<boolean>} opts.applyParsed
 *        Called with the parsed slot values (vehicle, distance, destination,
 *        etc.). Must return true if anything was applied.
 * @param {(acc:object)=>string|null} opts.nextMissingSlot
 *        Given the accumulated params, returns the first missing REQUIRED
 *        slot name ("distance" | "destination") or null.
 * @param {()=>void} opts.onConfirm   Fired when the user explicitly confirms
 *                                    the accumulated command (yes/ok).
 * @param {boolean} opts.externalGeneratingFlag  When flipped to true while
 *        we are in awaiting_confirm, treat it as manual confirmation and
 *        clear the session (user tapped GENERA manually).
 */
export function useVoiceConfig({ lang, t, applyParsed, nextMissingSlot, onConfirm, externalGeneratingFlag }) {
  const [voiceStage, setVoiceStage] = useState("idle"); // idle | listening | processing | awaiting_slot | awaiting_confirm
  const [voiceTranscript, setVoiceTranscript] = useState("");
  const [voiceHint, setVoiceHint] = useState("");
  const [pendingParams, setPendingParams] = useState({});
  const [missingSlot, setMissingSlot] = useState(null);

  const voiceRecogRef = useRef(null);
  const pendingParamsRef = useRef({});
  const missingSlotRef = useRef(null);
  const voiceStageRef = useRef("idle");

  const setStage = (s) => {
    voiceStageRef.current = s;
    setVoiceStage(s);
  };

  const clearVoiceSession = () => {
    setVoiceTranscript("");
    setVoiceHint("");
    pendingParamsRef.current = {};
    setPendingParams({});
    missingSlotRef.current = null;
    setMissingSlot(null);
    voiceStageRef.current = "idle";
    setVoiceStage("idle");
  };

  // TTS with completion callback
  const speakThen = (text, onEnd) => {
    if (typeof window === "undefined" || !window.speechSynthesis) {
      onEnd?.();
      return;
    }
    try {
      const synth = window.speechSynthesis;
      const u = new SpeechSynthesisUtterance(text);
      u.lang = lang === "en" ? "en-US" : "it-IT";
      u.rate = 1.05;
      u.onend = () => onEnd?.();
      u.onerror = () => onEnd?.();
      // iOS Safari quirk: after cancel() the engine can enter a paused state
      // that swallows the next speak(). resume() after cancel() forces it back
      // to a ready state before we queue the new utterance.
      synth.cancel();
      if (synth.paused) synth.resume();
      synth.speak(u);
    } catch {
      onEnd?.();
    }
  };
  const speakConfirm = (text) => speakThen(text, null);

  const mergePending = (parsed) => {
    const next = { ...pendingParamsRef.current };
    for (const [k, v] of Object.entries(parsed || {})) {
      if (v !== undefined && v !== null && v !== "") next[k] = v;
    }
    pendingParamsRef.current = next;
    setPendingParams(next);
    return next;
  };

  const slotPromptText = (slot) => {
    if (lang === "en") {
      switch (slot) {
        case "distance":
          return "How many kilometers? Or tell me a duration, like two hours.";
        case "destination":
          return "Where do you want to go?";
        default:
          return "Can you repeat?";
      }
    }
    switch (slot) {
      case "distance":
        return "Quanti chilometri vuoi fare? Oppure dimmi il tempo, ad esempio due ore.";
      case "destination":
        return "Dove vuoi andare?";
      default:
        return "Puoi ripetere?";
    }
  };

  const humanizeParams = (p) => {
    const parts = [];
    if (lang === "en") {
      if (p.vehicle === "moto") parts.push("motorbike");
      else if (p.vehicle === "ebike") parts.push("e-bike");
      if (p.activeTool === "destination" && (p.__destResolved || p.destination)) {
        parts.push(`destination ${p.__destResolved || p.destination}`);
      } else if (p.activeTool === "loop") {
        parts.push("loop");
      }
      if (typeof p.distance === "number") parts.push(`${p.distance} kilometers`);
      if (typeof p.timeH === "number") parts.push(`${p.timeH} hours`);
      if (typeof p.offroad === "number") parts.push(`${p.offroad}% off-road`);
      if (p.difficultyPref === "easy") parts.push("easy");
      else if (p.difficultyPref === "medium") parts.push("medium");
      else if (p.difficultyPref === "hard") parts.push("hard");
      return parts.join(", ");
    }
    if (p.vehicle === "moto") parts.push("moto");
    else if (p.vehicle === "ebike") parts.push("bici elettrica");
    if (p.activeTool === "destination" && (p.__destResolved || p.destination)) {
      parts.push(`destinazione ${p.__destResolved || p.destination}`);
    } else if (p.activeTool === "loop") {
      parts.push("anello");
    }
    if (typeof p.distance === "number") parts.push(`${p.distance} chilometri`);
    if (typeof p.timeH === "number") parts.push(`${p.timeH} ore`);
    if (typeof p.offroad === "number") parts.push(`${p.offroad}% off-road`);
    if (p.difficultyPref === "easy") parts.push("facile");
    else if (p.difficultyPref === "medium") parts.push("media");
    else if (p.difficultyPref === "hard") parts.push("difficile");
    return parts.join(", ");
  };

  const openMic = () => {
    const SR =
      (typeof window !== "undefined" &&
        (window.SpeechRecognition || window.webkitSpeechRecognition)) ||
      null;
    if (!SR) {
      toast.error(lang === "en" ? "Voice recognition not supported by your browser" : "Riconoscimento vocale non supportato dal tuo browser");
      setStage("idle");
      return;
    }
    const recog = new SR();
    recog.lang = lang === "en" ? "en-US" : "it-IT";
    recog.interimResults = true;
    recog.continuous = false;
    recog.maxAlternatives = 1;
    recog._lastFinal = "";
    recog.onresult = (e) => {
      let finalT = "";
      let interimT = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const tr = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalT += tr;
        else interimT += tr;
      }
      const text = (finalT || interimT).trim();
      setVoiceTranscript(text);
      recog._lastFinal = text;
    };
    recog.onend = () => {
      const text = (recog._lastFinal || "").trim();
      if (!text) {
        if (
          voiceStageRef.current === "awaiting_slot" ||
          voiceStageRef.current === "awaiting_confirm"
        ) {
          setTimeout(() => openMic(), 200);
          return;
        }
        setStage("idle");
        return;
      }
      handleUtterance(text);
    };
    recog.onerror = (e) => {
      if (e.error === "aborted") return;
      if (e.error === "no-speech") {
        if (
          voiceStageRef.current === "awaiting_slot" ||
          voiceStageRef.current === "awaiting_confirm"
        ) {
          setTimeout(() => openMic(), 200);
          return;
        }
      }
      setStage("idle");
      if (e.error !== "aborted" && e.error !== "no-speech") {
        toast.error(`Voce: ${e.error}`);
      }
    };
    voiceRecogRef.current = recog;
    try {
      recog.start();
      setStage("listening");
    } catch {
      setTimeout(() => openMic(), 400);
    }
  };

  const handleUtterance = async (transcript) => {
    setStage("processing");

    // Confirmation stage: parse yes / no / new params
    if (
      voiceStageRef.current === "awaiting_confirm" ||
      (voiceStageRef.current === "processing" &&
        missingSlotRef.current === null &&
        Object.keys(pendingParamsRef.current).length > 0)
    ) {
      if (AFFIRM_RE.test(transcript)) {
        setVoiceHint(t("voiceConfirmed"));
        speakConfirm(lang === "en" ? "Confirmed. Generating route." : "Confermato. Genero il percorso.");
        setStage("idle");
        setTimeout(() => {
          onConfirm?.();
          setTimeout(() => clearVoiceSession(), 400);
        }, 500);
        return;
      }
      if (NEGATE_RE.test(transcript)) {
        setVoiceHint(t("voiceCancelled"));
        speakConfirm(lang === "en" ? "Cancelled." : "Annullato.");
        pendingParamsRef.current = {};
        setPendingParams({});
        setStage("idle");
        return;
      }
      // Fallthrough → treat as new params to merge
    }

    try {
      const { data } = await api.post("/voice/parse", { transcript });
      const parsed = data?.parsed || {};

      // If we're filling a specific slot and the answer is a bare number,
      // route it to the right field.
      if (
        missingSlotRef.current === "distance" &&
        typeof parsed.distance !== "number" &&
        typeof parsed.timeH !== "number"
      ) {
        const num = transcript.match(/(\d{1,3}(?:[.,]\d+)?)/);
        if (num) {
          const val = parseFloat(num[1].replace(",", "."));
          if (/\bore|\bh\b|ora|\bhours?\b/i.test(transcript)) {
            parsed.timeH = Math.max(0.5, Math.min(8, val));
            parsed.paramMode = "time";
          } else {
            parsed.distance = Math.max(2, Math.min(200, Math.round(val)));
            parsed.paramMode = "distance";
          }
        }
      }
      if (missingSlotRef.current === "destination" && !parsed.destination) {
        // Treat the whole utterance as the destination text
        parsed.destination = transcript
          .replace(/^(vado|verso|a|al|allo|alla|per|fino a|to|towards?|toward)\s+/i, "")
          .trim();
        parsed.activeTool = "destination";
      }

      const applied = await applyParsed(parsed);
      if (!applied) {
        setVoiceHint(t("voiceNotUnderstood"));
        speakThen(lang === "en" ? "Didn't catch that. Try again." : "Non ho capito. Riprova.", () => openMic());
        return;
      }

      const merged = mergePending(parsed);
      const slot = nextMissingSlot(merged);

      if (slot) {
        missingSlotRef.current = slot;
        setMissingSlot(slot);
        const heard = humanizeParams(merged);
        const prompt = heard
          ? (lang === "en"
              ? `Got ${heard}. ${slotPromptText(slot)}`
              : `Ho capito ${heard}. ${slotPromptText(slot)}`)
          : slotPromptText(slot);
        setVoiceHint(prompt);
        setStage("awaiting_slot");
        speakThen(prompt, () => openMic());
        return;
      }

      // All required slots filled — ask for explicit confirmation
      missingSlotRef.current = null;
      setMissingSlot(null);
      const summary = humanizeParams(merged);
      const prompt = lang === "en"
        ? `Set: ${summary}. Shall I generate the route?`
        : `Impostato: ${summary}. Procedo con la generazione?`;
      setVoiceHint(prompt);
      setStage("awaiting_confirm");
      speakThen(prompt, () => openMic());
    } catch (e) {
      toast.error(lang === "en" ? "Voice parsing error" : "Errore parsing vocale");
      setStage("idle");
    }
  };

  // Public: toggle button — start fresh session or abort current one.
  const startVoiceCommand = () => {
    if (voiceStageRef.current !== "idle") {
      try {
        voiceRecogRef.current?.stop();
      } catch { /* ignored */ }
      if (typeof window !== "undefined" && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
      clearVoiceSession();
      return;
    }
    // iOS Safari quirk: SpeechSynthesis requires a direct user gesture to
    // unlock the audio channel. Prime it now inside the tap handler.
    if (typeof window !== "undefined" && window.speechSynthesis) {
      try {
        window.speechSynthesis.cancel();
        const primer = new SpeechSynthesisUtterance(" ");
        primer.volume = 0;
        primer.rate = 1;
        primer.lang = lang === "en" ? "en-US" : "it-IT";
        window.speechSynthesis.speak(primer);
      } catch { /* ignored */ }
    }
    clearVoiceSession();
    setVoiceHint(t("voiceListening"));
    openMic();
  };

  // If the user manually clicks GENERATE while in awaiting_confirm, treat it
  // as confirmation and clear the voice session.
  useEffect(() => {
    if (externalGeneratingFlag && voiceStageRef.current === "awaiting_confirm") {
      if (typeof window !== "undefined" && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
      clearVoiceSession();
    }
  }, [externalGeneratingFlag]);

  // Pre-load voices (Safari needs this) once
  useEffect(() => {
    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.getVoices();
    }
  }, []);

  return {
    voiceStage,
    voiceTranscript,
    voiceHint,
    pendingParams,
    missingSlot,
    startVoiceCommand,
    clearVoiceSession,
  };
}
